import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

interface SlimGridMotionProps {
  items?: string[];
  gradientColor?: string;
  pageKey?: string; // For database-backed pages (e.g., 'shop', 'signage', 'pavement', etc.)
}

const SlimGridMotion: React.FC<SlimGridMotionProps> = ({
  items = [],
  gradientColor = 'black',
  pageKey
}) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const mouseXRef = useRef(window.innerWidth / 2);

  const totalItems = 21; // 3 rows × 7 columns
  const combinedItems = items.length > 0 ? items.slice(0, totalItems) : Array.from({ length: totalItems }, (_, index) => `Item ${index + 1}`);

  useEffect(() => {
    gsap.ticker.lagSmoothing(0);

    const handleMouseMove = (e: MouseEvent) => {
      mouseXRef.current = e.clientX;
    };

    const updateMotion = () => {
      const maxMoveAmount = 200; // Reduced from 300
      const baseDuration = 0.8;
      const inertiaFactors = [0.6, 0.4, 0.3];

      rowRefs.current.forEach((row, index) => {
        if (row) {
          const direction = index % 2 === 0 ? 1 : -1;
          const moveAmount = ((mouseXRef.current / window.innerWidth) * maxMoveAmount - maxMoveAmount / 2) * direction;

          gsap.to(row, {
            x: moveAmount,
            duration: baseDuration + inertiaFactors[index % inertiaFactors.length],
            ease: 'power3.out',
            overwrite: 'auto'
          });
        }
      });
    };

    const removeAnimationLoop = gsap.ticker.add(updateMotion);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      removeAnimationLoop();
    };
  }, []);

  return (
    <div ref={gridRef} className="h-full w-full overflow-hidden">
      <section
        className="w-full h-full overflow-hidden relative flex items-center justify-center"
        style={{
          background: `radial-gradient(circle, ${gradientColor} 60%, transparent 100%)`
        }}>
        <div className="absolute inset-0 pointer-events-none z-[4] bg-[length:250px]"></div>
        <div
          className="gap-3 flex-none relative w-[150vw] h-full grid grid-rows-3 grid-cols-1 rotate-[-12deg] origin-center z-[2]">
          {[...Array(3)].map((_, rowIndex) => (
            <div
              key={rowIndex}
              className="grid gap-3 grid-cols-7"
              style={{ willChange: 'transform, filter' }}
              ref={el => { rowRefs.current[rowIndex] = el; }}>
              {[...Array(7)].map((_, itemIndex) => {
                const content = combinedItems[rowIndex * 7 + itemIndex];
                return (
                  <div key={itemIndex} className="relative">
                    <div
                      className="relative w-full h-full overflow-hidden rounded-lg bg-[#111] flex items-center justify-center text-white text-base">
                      {typeof content === 'string' && (content.startsWith('http') || content.startsWith('/') || content.endsWith('.png') || content.endsWith('.jpg') || content.endsWith('.jpeg') || content.endsWith('.webp')) ? (
                        <div
                          className="w-full h-full bg-cover bg-center absolute top-0 left-0"
                          style={{ backgroundImage: `url(${content})` }}></div>
                      ) : (
                        <div className="p-3 text-center z-[1]">{content}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="relative w-full h-full top-0 left-0 pointer-events-none"></div>
      </section>
    </div>
  );
};

export default SlimGridMotion;
